/**
 * The category rules an editor has to obey, read from the write authority.
 *
 * `profile-fields` owns the two numbers that matter — at most
 * `MAX_PROFILE_CATEGORIES`, exactly one primary — and `assertProfileCategories`
 * is the verdict the server itself uses. Nothing here restates either rule: the
 * reducers below keep a selection valid BY CONSTRUCTION (the first pick becomes
 * primary, removing the primary promotes the next, a full selection stops
 * accepting picks) and `categoryIssue` asks the shared schema for the last word
 * before a save is offered.
 *
 * The reducers are pure and never mutate their input, so an optimistic UI can
 * hold the previous array as its own rollback.
 */

import { MAX_PROFILE_CATEGORIES, assertProfileCategories } from './profile-fields';

/** Exactly `CategoryInput` — what `updateProfile` takes. */
export type CategoryChoice = {
  categoryId: string;
  isPrimary: boolean;
};

/** A row of the `categories` catalog, as the query returns it. */
export type CategoryOption = {
  id: string;
  name: string;
  slug?: string | null;
  isActive?: boolean | null;
  parentId?: string | null;
  parent?: { id?: string; name?: string | null } | null;
  facet?: { code?: string | null; name?: string | null } | null;
};

export type CategoryFacet = {
  code: string;
  name: string;
};

export type CategoryGroup = CategoryFacet & {
  options: Array<{ id: string; name: string; parentName: string | null }>;
};

/** A profile's stored categories carry the catalog id under `categoryId`. */
export type StoredCategory = {
  categoryId?: string | null;
  id?: string | null;
  name?: string | null;
  slug?: string | null;
  isPrimary?: boolean | null;
  facetCode?: string | null;
};

/**
 * Facet display order, unchanged from the editor this replaces: an artist picks
 * a medium before a style before a subject. Anything else sorts last.
 */
const FACET_ORDER: Record<string, number> = { medium: 1, style: 2, subject: 3 };

const UNKNOWN_FACET = 'unknown';

/** Reads a profile's stored categories as an editable selection. */
export function chosenCategories(stored: StoredCategory[] | null | undefined): CategoryChoice[] {
  if (!stored?.length) {
    return [];
  }
  return stored.reduce<CategoryChoice[]>((memo, category) => {
    const categoryId = category.categoryId ?? category.id ?? '';
    if (!categoryId) {
      return memo;
    }
    memo.push({ categoryId, isPrimary: category.isPrimary === true });
    return memo;
  }, []);
}

/** A category as the header chips render it. */
export type CategoryChip = {
  id: string;
  categoryId: string;
  name: string;
  slug?: string | null;
  isPrimary: boolean;
  facetCode?: string | null;
};

/**
 * Normalises stored categories for rendering: a row with no id or no name is
 * not displayable, so it is dropped rather than rendered as a blank chip.
 */
export function categoryChips(stored: StoredCategory[] | null | undefined): CategoryChip[] {
  if (!stored?.length) {
    return [];
  }
  return stored.reduce<CategoryChip[]>((memo, category) => {
    const categoryId = category.categoryId ?? category.id ?? '';
    const name = (category.name ?? '').trim();
    if (!categoryId || !name) {
      return memo;
    }
    memo.push({
      id: category.id ?? categoryId,
      categoryId,
      name,
      isPrimary: category.isPrimary === true,
      ...(category.slug ? { slug: category.slug } : {}),
      ...(category.facetCode ? { facetCode: category.facetCode } : {}),
    });
    return memo;
  }, []);
}

export function isCategoryChosen(chosen: CategoryChoice[], categoryId: string): boolean {
  return chosen.some((category) => category.categoryId === categoryId);
}

export function canAddCategory(chosen: CategoryChoice[]): boolean {
  return chosen.length < MAX_PROFILE_CATEGORIES;
}

/** The first pick is the primary, so a selection is never left without one. */
export function addCategory(chosen: CategoryChoice[], categoryId: string): CategoryChoice[] {
  if (!categoryId || isCategoryChosen(chosen, categoryId) || !canAddCategory(chosen)) {
    return chosen;
  }
  return [...chosen, { categoryId, isPrimary: chosen.length === 0 }];
}

/**
 * Removing the primary promotes whatever is now first — never zero primaries.
 *
 * Removing anything ELSE leaves the primary alone. `setPrimaryCategory` does not
 * reorder, so the primary is not necessarily first: re-stamping every survivor would
 * silently move the primary to the head of the list because an unrelated chip was
 * deleted, and that reassignment is what gets saved.
 */
export function removeCategory(chosen: CategoryChoice[], categoryId: string): CategoryChoice[] {
  const remaining = chosen.filter((category) => category.categoryId !== categoryId);
  if (remaining.length === chosen.length) {
    return chosen;
  }
  if (!remaining.length) {
    return remaining;
  }
  if (remaining.some((category) => category.isPrimary)) {
    return remaining;
  }
  return remaining.map((category, index) => ({ ...category, isPrimary: index === 0 }));
}

export function setPrimaryCategory(chosen: CategoryChoice[], categoryId: string): CategoryChoice[] {
  if (!isCategoryChosen(chosen, categoryId)) {
    return chosen;
  }
  return chosen.map((category) => ({ ...category, isPrimary: category.categoryId === categoryId }));
}

/**
 * Folds AI suggestions into the current selection: already-chosen ids are
 * skipped and the cap still applies, so a suggestion can add to a selection but
 * never replace or overflow it. Matches the editor this replaces, which merged
 * rather than overwrote.
 */
export function mergeCategorySuggestions(chosen: CategoryChoice[], suggested: readonly string[]): CategoryChoice[] {
  return suggested.reduce((memo, categoryId) => addCategory(memo, categoryId), chosen);
}

/** The shared verdict, as a message — `null` when the selection is savable. */
export function categoryIssue(chosen: CategoryChoice[]): string | null {
  try {
    assertProfileCategories(chosen);
    return null;
  } catch (reason) {
    return reason instanceof Error ? reason.message : 'Invalid categories';
  }
}

/** The minimum a catalog row needs to be matchable against a generated name. */
export interface MatchableCategory {
  id: string;
  name: string;
}

/**
 * Caps an imported category list and names exactly one primary.
 *
 * Import matches catalog rows first, then stamps: if nothing is already
 * primary, the first medium-facet row wins; otherwise the first row. Claim
 * preserves a blob primary when present. Empty input stays empty (clear).
 */
export function stampImportedCategories<T extends { facetCode?: string | null; isPrimary?: boolean | null }>(
  categories: T[]
): Array<T & { isPrimary: boolean }> {
  const capped = categories.slice(0, MAX_PROFILE_CATEGORIES);
  if (capped.length === 0) {
    return [];
  }

  let primaryIndex = capped.findIndex((category) => category.isPrimary === true);
  if (primaryIndex < 0) {
    primaryIndex = capped.findIndex((category) => category.facetCode === 'medium');
  }
  if (primaryIndex < 0) {
    primaryIndex = 0;
  }

  return capped.map((category, index) => ({
    ...category,
    isPrimary: index === primaryIndex,
  }));
}

/**
 * Fuzzy-matches AI-generated category names back onto the catalog. Moved here
 * from `client/src/utils/matchGeneratedCategories.ts` (which now re-exports this
 * module) so the legacy editor, the welcome flow and the profile page's inline
 * editor all match identically — and so the cap is `MAX_PROFILE_CATEGORIES`
 * rather than a hardcoded number.
 *
 * The match is intentionally permissive (case-insensitive bidirectional
 * `includes` against the category name) — the upstream LLM rarely returns the
 * exact catalog string, but its phrasing typically overlaps.
 */
export function matchGeneratedCategories<T extends MatchableCategory>(
  available: readonly T[],
  generatedNames: readonly string[],
  limit = MAX_PROFILE_CATEGORIES
): T[] {
  if (!generatedNames.length || !available.length) return [];

  const normalizedGenerated = generatedNames.map((n) => n?.trim().toLowerCase()).filter((n): n is string => !!n);

  if (!normalizedGenerated.length) return [];

  return available
    .filter((cat) => {
      const catNameLower = cat.name.toLowerCase();
      return normalizedGenerated.some(
        (genNameLower) => catNameLower.includes(genNameLower) || genNameLower.includes(catNameLower)
      );
    })
    .slice(0, limit);
}

function facetOf(option: CategoryOption): CategoryFacet {
  const code = option.facet?.code ?? UNKNOWN_FACET;
  return { code, name: option.facet?.name ?? code };
}

/** The facet filter pills: every facet the catalog actually offers, in order. */
export function categoryFacets(catalog: readonly CategoryOption[]): CategoryFacet[] {
  const byCode = new Map<string, CategoryFacet>();
  catalog.forEach((option) => {
    if (option.isActive === false) {
      return;
    }
    const facet = facetOf(option);
    if (facet.code === UNKNOWN_FACET || byCode.has(facet.code)) {
      return;
    }
    byCode.set(facet.code, facet);
  });
  return [...byCode.values()].sort(byFacet);
}

function byFacet(a: CategoryFacet, b: CategoryFacet): number {
  const order = (FACET_ORDER[a.code] ?? 999) - (FACET_ORDER[b.code] ?? 999);
  if (order !== 0) {
    return order;
  }
  return a.name.localeCompare(b.name);
}

/**
 * The browsable list: inactive rows and rows with no facet are dropped (they are
 * not offerable), a search matches on the category's own name or its parent's,
 * and each group is one facet with its options sorted by name. The parent name
 * rides along as a label so a child like "Oil" reads as "Painting / Oil".
 */
export function categoryGroups(
  catalog: readonly CategoryOption[],
  filter?: { search?: string; facetCode?: string }
): CategoryGroup[] {
  const search = (filter?.search ?? '').trim().toLowerCase();
  const facetCode = filter?.facetCode && filter.facetCode !== 'all' ? filter.facetCode : null;
  const parents = new Map(catalog.map((option) => [option.id, option.name]));

  const groups = catalog.reduce<Map<string, CategoryGroup>>((memo, option) => {
    if (option.isActive === false) {
      return memo;
    }
    const facet = facetOf(option);
    if (facet.code === UNKNOWN_FACET) {
      return memo;
    }
    if (facetCode && facet.code !== facetCode) {
      return memo;
    }
    const parentName = option.parent?.name ?? (option.parentId ? (parents.get(option.parentId) ?? null) : null);
    if (search && !`${option.name} ${parentName ?? ''}`.toLowerCase().includes(search)) {
      return memo;
    }
    const group = memo.get(facet.code) ?? { ...facet, options: [] };
    group.options.push({ id: option.id, name: option.name, parentName });
    memo.set(facet.code, group);
    return memo;
  }, new Map());

  return [...groups.values()]
    .map((group) => ({ ...group, options: group.options.sort((a, b) => a.name.localeCompare(b.name)) }))
    .sort(byFacet);
}
